/**
 * The "new work arrived" chime and its per-browser mute switch.
 *
 * - The tone is generated with the Web Audio API (two short sine partials,
 *   ~250 ms, quiet) — there is no audio file to ship or cache.
 * - Autoplay policy: an AudioContext is only created/resumed after the first
 *   user gesture (pointerdown/keydown). Until then `playChime` is a silent no-op,
 *   never a console error and never a queued sound that fires later.
 * - Sound is ON by default; the mute choice is a per-browser convenience kept in
 *   localStorage (every access guarded — private mode just means "on").
 * - Several tabs: a BroadcastChannel lets the tab with the lowest id play for the
 *   same (key, value) within a short window. Best effort; any failure falls back
 *   to playing in this tab.
 */
import { useSyncExternalStore } from 'react';

export const SOUND_STORAGE_KEY = 'ithelpdesk.sound';
const CHANNEL_NAME = 'ithelpdesk.chime';
/** How long a tab waits for a competing claim from another tab before playing. */
export const CLAIM_WAIT_MS = 120;

// ---- mute preference --------------------------------------------------------

function readMuted(): boolean {
  try {
    const raw = window.localStorage.getItem(SOUND_STORAGE_KEY);
    return raw !== null && JSON.parse(raw) === 'off';
  } catch {
    return false;
  }
}

let muted = typeof window === 'undefined' ? false : readMuted();
const listeners = new Set<() => void>();

export function isSoundMuted(): boolean {
  return muted;
}

export function setSoundMuted(next: boolean): void {
  muted = next;
  try {
    window.localStorage.setItem(SOUND_STORAGE_KEY, JSON.stringify(next ? 'off' : 'on'));
  } catch {
    // Storage unavailable: the choice still applies for this page.
  }
  listeners.forEach((l) => l());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  const onStorage = (e: StorageEvent): void => {
    if (e.key !== SOUND_STORAGE_KEY) return;
    muted = readMuted();
    listener();
  };
  window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(listener);
    window.removeEventListener('storage', onStorage);
  };
}

/** `[muted, setMuted]` backed by the shared store (user menu toggle). */
export function useSoundMuted(): [boolean, (next: boolean) => void] {
  const value = useSyncExternalStore(subscribe, () => muted, () => false);
  return [value, setSoundMuted];
}

// ---- audio unlock + tone ----------------------------------------------------

interface AudioWindow {
  AudioContext?: typeof AudioContext;
  webkitAudioContext?: typeof AudioContext;
}

let context: AudioContext | null = null;
let unlocked = false;
let unlockInstalled = false;

function ensureContext(): AudioContext | null {
  if (context) return context;
  try {
    const w = window as unknown as AudioWindow;
    const Ctor = w.AudioContext ?? w.webkitAudioContext;
    if (!Ctor) return null;
    context = new Ctor();
    return context;
  } catch {
    return null;
  }
}

function unlock(): void {
  if (unlocked) return;
  const ctx = ensureContext();
  if (!ctx) return;
  unlocked = true;
  removeUnlockListeners();
  try {
    void ctx.resume?.().catch(() => undefined);
  } catch {
    // Ignore: playChime retries the resume.
  }
}

function removeUnlockListeners(): void {
  document.removeEventListener('pointerdown', unlock, true);
  document.removeEventListener('keydown', unlock, true);
}

/** Registers the one-time first-gesture listeners. Safe to call repeatedly. */
export function installAudioUnlock(): void {
  if (unlockInstalled || typeof document === 'undefined') return;
  unlockInstalled = true;
  document.addEventListener('pointerdown', unlock, true);
  document.addEventListener('keydown', unlock, true);
}

/** True once a user gesture has created the AudioContext (for tests / diagnostics). */
export function isAudioUnlocked(): boolean {
  return unlocked;
}

/** Plays the ~250 ms chime now. Silent when muted, locked, or unsupported. */
export function playChime(): boolean {
  if (muted || !unlocked || !context) return false;
  try {
    const ctx = context;
    if (ctx.state === 'suspended') void ctx.resume().catch(() => undefined);
    const t0 = ctx.currentTime;
    const master = ctx.createGain();
    master.gain.setValueAtTime(0.0001, t0);
    master.gain.exponentialRampToValueAtTime(0.08, t0 + 0.02);
    master.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.25);
    master.connect(ctx.destination);
    // A rising major third: friendly, short, not alarming.
    [
      [880, 0],
      [1174.66, 0.09],
    ].forEach(([freq, offset]) => {
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq as number, t0 + (offset as number));
      osc.connect(master);
      osc.start(t0 + (offset as number));
      osc.stop(t0 + 0.26);
    });
    return true;
  } catch {
    return false;
  }
}

// ---- cross-tab de-duplication ----------------------------------------------

const tabId = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
let channel: BroadcastChannel | null | undefined;
const outranked = new Set<string>();
/** eventKey -> when it was last handled. A repeat inside the window is a replay, not a new event. */
const played = new Map<string, number>();
const REPLAY_WINDOW_MS = 10_000;

function getChannel(): BroadcastChannel | null {
  if (channel !== undefined) return channel;
  try {
    if (typeof BroadcastChannel === 'undefined') {
      channel = null;
    } else {
      channel = new BroadcastChannel(CHANNEL_NAME);
      channel.onmessage = (e: MessageEvent<unknown>) => {
        const d = e.data as { k?: unknown; id?: unknown } | null;
        if (d && typeof d.k === 'string' && typeof d.id === 'string' && d.id < tabId) outranked.add(d.k);
      };
    }
  } catch {
    channel = null;
  }
  return channel;
}

/**
 * Plays the chime once for a genuine increase identified by `eventKey`
 * (`<query key>:<new value>`). The same key is never played twice in this
 * tab, and with several tabs open only the lowest-id tab plays.
 */
export function chimeOnce(eventKey: string): void {
  const now = Date.now();
  const last = played.get(eventKey);
  if (last !== undefined && now - last < REPLAY_WINDOW_MS) return;
  played.set(eventKey, now);
  if (played.size > 200) played.delete(played.keys().next().value as string);
  const ch = getChannel();
  if (!ch) {
    playChime();
    return;
  }
  try {
    ch.postMessage({ k: eventKey, id: tabId });
  } catch {
    playChime();
    return;
  }
  setTimeout(() => {
    if (outranked.delete(eventKey)) return;
    playChime();
  }, CLAIM_WAIT_MS);
}

/** Test seam: forget module state. */
export function __resetNotificationSoundForTests(): void {
  played.clear();
  outranked.clear();
  try {
    channel?.close();
  } catch {
    // ignore
  }
  channel = undefined;
  context = null;
  unlocked = false;
  unlockInstalled = false;
  removeUnlockListeners();
}
